import React, { useMemo, useState } from 'react';
import { isFinite, toNumber, uniqBy } from 'lodash';
import { Check } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from 'shadcn/components/ui/DropdownMenu';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';

import { FilterChip } from './FilterChip.jsx';

function NumberFilter({ id, label, onChange, placeholder, value }) {
    return (
        <div className="flex flex-col gap-1">
            {label && <Label htmlFor={id}>{label}</Label>}
            <Input
                id={id}
                type="number"
                placeholder={placeholder}
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
            />
        </div>
    );
}

export { NumberFilter };

// Menu variant: wraps the input inside a submenu panel
function NumberFilterMenuItem({ id, label, onChange, placeholder, value }) {
    const [draft, setDraft] = useState('');
    const list = useMemo(() => (Array.isArray(value) ? value : value != null && value !== '' ? [value] : []), [value]);

    const addDraft = () => {
        if (draft === '') return;
        const num = toNumber(draft);
        if (!isFinite(num)) return;
        const next = uniqBy([...list, num], (v) => toNumber(v));
        onChange(next);
        setDraft('');
    };

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger inset>{label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="p-0">
                <div className="px-3 py-3 w-[360px]">
                    <div className="flex flex-col gap-1">
                        {label && <Label htmlFor={id}>{label}</Label>}
                        <div className="flex items-center gap-2">
                            <Input
                                id={id}
                                type="number"
                                placeholder={placeholder}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addDraft();
                                    }
                                }}
                                className="h-8 px-2 text-sm"
                            />
                            <Button size="iconSm" variant="secondary" aria-label="Queue number" onClick={addDraft}>
                                <Check />
                            </Button>
                        </div>
                        {list && list.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                                {list.map((v, idx) => (
                                    <FilterChip
                                        key={`${id}-num-${idx}`}
                                        label={String(v)}
                                        deletable
                                        onDelete={() => {
                                            const num = Number(v);
                                            const next = list.filter((x) => Number(x) !== num);
                                            onChange(next.length > 0 ? next : undefined);
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
}

export { NumberFilterMenuItem };
